import { useState, useEffect } from "react";
import api from "../lib/api";
import {
  Package, AlertTriangle, Plus, Search, ArrowDown, ArrowUp,
  Edit2, Trash2, DollarSign, TrendingUp, FileText,
  Send, Check, RefreshCw, ShoppingBag, Settings2, Upload,
  Truck, Download, X, Filter, Minus, History
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import BulkImportDialog from "../components/BulkImportDialog";
import ImportHistoryDialog from "../components/ImportHistoryDialog";
import { Switch } from "../components/ui/switch";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import VendorsTab from "./VendorsTab";
import InventoryCountsTab from "../components/InventoryCountsTab";

const COLORS = ["#1A1A1A", "#B5A493", "#5E5E5E", "#8A8A8A", "#E6E4E0", "#2D5936", "#805B10", "#274F7D"];

export default function InventoryPage() {
  const [products, setProducts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [valueData, setValueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [showAdjust, setShowAdjust] = useState(null);
  const [adjustQty, setAdjustQty] = useState(0);
  const [adjustReason, setAdjustReason] = useState("restock");
  const [newProduct, setNewProduct] = useState({ name: "", category: "", price: 0, cost: 0, sku: "", stock: 0, low_stock_threshold: 10, reorder_point: 10, reorder_qty: 20, vendor: "", location_ids: [], storage_location: "" });
  const [locations, setLocations] = useState([]);
  // Filter chip at the top of the page — narrows the product list to a
  // single salon location (or "all"). The backend already supports the
  // location_id query param via `location_ids` containment so the
  // filter is just a UI surface for an existing capability.
  const [activeLocationId, setActiveLocationId] = useState("all");
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [reorderSettings, setReorderSettings] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [editSetting, setEditSetting] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  // Tab switcher between stock management and the new Vendors module
  const [activeTab, setActiveTab] = useState("stock");
  // Quick Check-In drawer — accept a vendor delivery in one shot
  const [showQuickReceive, setShowQuickReceive] = useState(false);
  // {product_id: qty} — the in-flight cart for the check-in drawer
  const [receiveCart, setReceiveCart] = useState({});
  const [receiveSearch, setReceiveSearch] = useState("");
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);
  // Filter mode for the main inventory table
  const [filterMode, setFilterMode] = useState("all"); // "all" | "needs_order"

  const fetchData = async () => {
    try {
      const [prodRes, alertRes, logRes, valRes, locRes, poRes, settingsRes] = await Promise.all([
        api.get("/inventory"), api.get("/inventory/alerts"), api.get("/inventory/logs?limit=20"),
        api.get("/inventory/value"), api.get("/locations"), api.get("/purchase-orders"),
        api.get("/reorder-settings")
      ]);
      setProducts(prodRes.data); setAlerts(alertRes.data);
      setLogs(logRes.data); setValueData(valRes.data); setLocations(locRes.data);
      setPurchaseOrders(poRes.data); setReorderSettings(settingsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    try {
      await api.post("/products", newProduct);
      toast.success("Product added");
      setShowNew(false);
      setNewProduct({ name: "", category: "", price: 0, cost: 0, sku: "", stock: 0, low_stock_threshold: 10, reorder_point: 10, reorder_qty: 20, vendor: "", location_ids: [], storage_location: "" });
      fetchData();
    } catch (err) { toast.error("Failed to create"); }
  };

  const handleUpdate = async () => {
    if (!editProduct) return;
    try {
      await api.put(`/products/${editProduct.product_id}`, {
        name: editProduct.name, category: editProduct.category, price: editProduct.price,
        cost: editProduct.cost, sku: editProduct.sku, low_stock_threshold: editProduct.low_stock_threshold,
        reorder_point: editProduct.reorder_point, reorder_qty: editProduct.reorder_qty, vendor: editProduct.vendor,
        location_ids: editProduct.location_ids || [],
        storage_location: editProduct.storage_location || "",
      });
      toast.success("Product updated");
      setEditProduct(null);
      fetchData();
    } catch (err) { toast.error("Failed to update"); }
  };

  const handleAdjust = async () => {
    if (!showAdjust || adjustQty === 0) return;
    try {
      await api.post(`/inventory/adjust?product_id=${showAdjust.product_id}&quantity=${adjustQty}&reason=${adjustReason}`);
      toast.success("Stock adjusted");
      setShowAdjust(null); setAdjustQty(0);
      fetchData();
    } catch (err) { toast.error("Failed"); }
  };

  // Submit the entire receive cart in one round-trip.
  const submitReceiveCart = async () => {
    const adjustments = Object.entries(receiveCart)
      .filter(([_, qty]) => Number(qty) > 0)
      .map(([product_id, qty]) => ({ product_id, quantity: Number(qty) }));
    if (adjustments.length === 0) { toast.error("Add at least one product"); return; }
    setReceiveSubmitting(true);
    try {
      const res = await api.post("/inventory/bulk-receive", { adjustments, reason: "restock" });
      const n = res.data?.received_count || 0;
      toast.success(`Received ${n} item${n === 1 ? "" : "s"} into stock`);
      setReceiveCart({});
      setReceiveSearch("");
      setShowQuickReceive(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to receive");
    } finally {
      setReceiveSubmitting(false);
    }
  };

  // Build a CSV the user can hand to their vendor — only the items
  // currently below PAR, with the suggested order quantity.
  const downloadReorderCSV = () => {
    const lines = [["Product", "SKU", "Vendor", "Current Stock", "PAR", "Order Qty", "Unit Cost", "Extended Cost"]];
    const reorderItems = products
      .filter(p => {
        const par = p.reorder_point || p.low_stock_threshold || 0;
        return par > 0 && (p.stock || 0) <= par;
      })
      .sort((a, b) => (a.vendor || "").localeCompare(b.vendor || ""));
    reorderItems.forEach(p => {
      const par = p.reorder_point || p.low_stock_threshold || 0;
      const qty = p.reorder_qty || Math.max(par - (p.stock || 0), 1);
      const ext = (qty * (p.cost || 0)).toFixed(2);
      lines.push([
        p.name || "",
        p.sku || "",
        p.vendor || "",
        String(p.stock || 0),
        String(par),
        String(qty),
        String(p.cost || 0),
        ext,
      ]);
    });
    if (lines.length === 1) {
      toast.info("Nothing to reorder right now — all stock is above PAR");
      return;
    }
    // Proper CSV escaping: wrap in quotes + double-up internal quotes
    const csv = lines
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reorder-list-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${lines.length - 1} item${lines.length - 1 === 1 ? "" : "s"} to CSV`);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      fetchData();
    } catch (err) { toast.error("Failed"); }
  };

  const autoGeneratePOs = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/purchase-orders/auto-generate");
      setGenResult(res.data);
      fetchData();
      if (res.data.generated > 0) {
        toast.success(`${res.data.generated} purchase order(s) generated`);
      } else {
        toast.info(res.data.message);
      }
    } catch (err) { toast.error("Failed to generate POs"); }
    finally { setGenerating(false); }
  };

  const approvePO = async (poId) => {
    try {
      await api.put(`/purchase-orders/${poId}/approve`);
      toast.success("PO sent to vendor");
      fetchData();
    } catch (err) { toast.error("Failed"); }
  };

  const receivePO = async (poId) => {
    try {
      await api.put(`/purchase-orders/${poId}/receive`);
      toast.success("PO received — stock updated");
      fetchData();
    } catch (err) { toast.error("Failed"); }
  };

  const deletePO = async (poId) => {
    try {
      await api.delete(`/purchase-orders/${poId}`);
      toast.success("PO deleted");
      fetchData();
    } catch (err) { toast.error("Failed"); }
  };

  const saveSetting = async () => {
    if (!editSetting) return;
    try {
      await api.post("/reorder-settings", editSetting);
      toast.success("Settings saved");
      setEditSetting(null);
      setShowSettings(false);
      fetchData();
    } catch (err) { toast.error("Failed"); }
  };

  // Filter pipeline: salon-location chip first, then search text, then
  // the optional "needs to order" gate.
  const filtered = products.filter(p => {
    if (activeLocationId !== "all") {
      const lids = p.location_ids || [];
      // Empty location_ids means "available everywhere" — keep it so
      // products that weren't tagged still show up in any location view.
      if (lids.length > 0 && !lids.includes(activeLocationId)) return false;
    }
    const searchHit = !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase()) || p.storage_location?.toLowerCase().includes(search.toLowerCase());
    if (!searchHit) return false;
    if (filterMode === "needs_order") {
      const par = p.reorder_point || p.low_stock_threshold || 0;
      return par > 0 && (p.stock || 0) <= par;
    }
    return true;
  });
  const needsOrderCount = products.filter(p => {
    const par = p.reorder_point || p.low_stock_threshold || 0;
    return par > 0 && (p.stock || 0) <= par;
  }).length;

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  const v = valueData || {};

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="inventory-page">
      <div className="flex items-center justify-between mb-6 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Inventory</h1>
          <p className="text-slate-600 text-sm mt-1">{v.total_products || 0} products, {v.total_units || 0} units in stock</p>
        </div>
        {activeTab === "stock" && (
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="quick-receive-btn" onClick={() => setShowQuickReceive(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 px-5 text-sm">
              <Truck className="w-4 h-4 mr-1.5" /> Quick Check-In
            </Button>
            <Button data-testid="vendor-settings-btn" onClick={() => setShowSettings(true)} variant="outline" className="border-slate-200 rounded-full h-10 px-4 text-sm">
              <Settings2 className="w-4 h-4 mr-1.5" /> Vendor Rules
            </Button>
            <Button data-testid="auto-generate-po-btn" onClick={autoGeneratePOs} disabled={generating} variant="outline" className="border-rose-300 text-slate-900 hover:bg-rose-50 rounded-full h-10 px-4 text-sm">
              <RefreshCw className={`w-4 h-4 mr-1.5 ${generating ? 'animate-spin' : ''}`} /> {generating ? "Scanning..." : "Auto-Reorder"}
            </Button>
            <Button data-testid="import-products-btn" variant="outline" onClick={() => setShowImport(true)} className="border-slate-200 rounded-full h-10 px-5 text-sm">
              <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            </Button>
            <Button data-testid="import-history-btn" variant="outline" onClick={() => setShowImportHistory(true)} className="border-slate-200 rounded-full h-10 px-4 text-sm" title="Recent imports / undo">
              <History className="w-4 h-4 mr-1.5" /> History
            </Button>
            <Button data-testid="new-product-btn" onClick={() => setShowNew(true)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 px-5 text-sm">
              <Plus className="w-4 h-4 mr-1.5" /> Add Product
            </Button>
          </div>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex items-center gap-1 mb-6 border-b border-slate-200" data-testid="inventory-tabs">
        {[
          { key: "stock", label: "Stock" },
          { key: "storage_map", label: "Storage Map" },
          { key: "counts", label: "Counts" },
          { key: "vendors", label: "Vendors" },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`relative h-10 px-5 text-sm font-medium transition-colors ${
              activeTab === t.key ? "text-slate-900" : "text-slate-500 hover:text-slate-700"
            }`}>
            {t.label}
            {activeTab === t.key && (
              <span className="absolute bottom-[-1px] left-3 right-3 h-[2px] bg-rose-500 rounded-full" />
            )}
          </button>
        ))}
      </div>

      {activeTab === "vendors" && <VendorsTab />}
      {activeTab === "counts" && <InventoryCountsTab products={products} />}
      {activeTab === "storage_map" && <StorageMapTab products={products} activeLocationId={activeLocationId} locations={locations} onJump={(loc) => { setSearch(loc); setActiveTab("stock"); }} />}

      {activeTab === "stock" && (<>
      {/* Value Dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Cost Value</span></div>
          <p className="font-['Outfit'] text-2xl font-medium text-slate-900">${(v.total_cost || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Retail Value</span></div>
          <p className="font-['Outfit'] text-2xl font-medium text-slate-900">${(v.total_retail || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-emerald-600" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Margin</span></div>
          <p className="font-['Outfit'] text-2xl font-medium text-emerald-600">{v.margin_pct || 0}%</p>
          <p className="text-xs text-slate-400">${(v.total_margin || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2"><Package className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Total Units</span></div>
          <p className="font-['Outfit'] text-2xl font-medium text-slate-900">{v.total_units || 0}</p>
        </div>
        <div className={`border rounded-2xl p-5 ${(v.below_par || 0) > 0 ? 'bg-[#FDF3E1] border-[#F3D8A2]' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className={`w-4 h-4 ${(v.below_par || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`} strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Below PAR</span></div>
          <p className={`font-['Outfit'] text-2xl font-medium ${(v.below_par || 0) > 0 ? 'text-amber-600' : 'text-slate-900'}`}>{v.below_par || 0}</p>
        </div>
      </div>

      {/* Category Breakdown */}
      {(v.by_category || []).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="category-chart">
            <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-4">Value by Category</h3>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={v.by_category} dataKey="retail_value" nameKey="category" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3}>
                    {(v.by_category || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(val) => `$${val.toFixed(0)}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-4">Category Summary</h3>
            <div className="space-y-3">
              {(v.by_category || []).map((cat, i) => (
                <div key={cat.category} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-sm text-slate-900 font-medium">{cat.category}</span>
                    <span className="text-xs text-slate-400">{cat.products} items</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-slate-900">${cat.retail_value?.toFixed(0)}</span>
                    <span className="text-xs text-slate-400 ml-2">{cat.units} units</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search + filter + export toolbar */}
      <div className="flex items-center gap-3 flex-wrap mb-6">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="inventory-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, SKU, or storage location..." className="pl-10 h-10 bg-white border-slate-200 rounded-lg" />
        </div>
        {/* Salon-location chips — surfaces a list of physical salons so
            the manager can flip between them. Only renders when the org
            actually has multiple locations to avoid wasted vertical
            space for single-location salons. */}
        {locations.length > 1 && (
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1" data-testid="inventory-location-filter">
            <button data-testid="loc-filter-all" onClick={() => setActiveLocationId("all")}
              className={`h-8 px-3 text-xs font-medium rounded-full transition-colors ${
                activeLocationId === "all" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}>
              All locations
            </button>
            {locations.map(loc => (
              <button key={loc.location_id}
                data-testid={`loc-filter-${loc.location_id}`}
                onClick={() => setActiveLocationId(loc.location_id)}
                className={`h-8 px-3 text-xs font-medium rounded-full transition-colors ${
                  activeLocationId === loc.location_id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
                }`}>
                {loc.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1" data-testid="inventory-filter">
          <button data-testid="filter-all" onClick={() => setFilterMode("all")}
            className={`h-8 px-4 text-xs font-medium rounded-full transition-colors ${
              filterMode === "all" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
            }`}>
            All products
          </button>
          <button data-testid="filter-needs-order" onClick={() => setFilterMode("needs_order")}
            className={`h-8 px-4 text-xs font-medium rounded-full transition-colors flex items-center gap-1.5 ${
              filterMode === "needs_order" ? "bg-amber-500 text-white" : "text-slate-600 hover:bg-amber-50"
            }`}>
            <AlertTriangle className="w-3 h-3" /> Needs to order
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
              filterMode === "needs_order" ? "bg-white/30" : "bg-amber-100 text-amber-700"
            }`}>{needsOrderCount}</span>
          </button>
        </div>
        <Button data-testid="export-reorder-csv-btn" onClick={downloadReorderCSV} variant="outline"
          className="border-slate-200 hover:bg-slate-50 rounded-full h-10 px-4 text-sm ml-auto">
          <Download className="w-4 h-4 mr-1.5" /> Download Vendor CSV
        </Button>
      </div>

      {/* Products Table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-8" data-testid="inventory-table">
        <table className="w-full">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-slate-200">
              <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Product</th>
              <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">SKU</th>
              <th className="text-left px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Vendor</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Cost</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Price</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Stock</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">PAR</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Reorder</th>
              <th className="text-right px-3 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Value</th>
              <th className="w-28"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const belowPar = p.stock <= (p.reorder_point || p.low_stock_threshold || 10);
              const margin = p.price > 0 ? ((p.price - (p.cost || 0)) / p.price * 100).toFixed(0) : 0;
              const stockValue = (p.stock || 0) * (p.price || 0);
              return (
                <tr key={p.product_id} className={`border-b border-slate-200 hover:bg-[#FAFAFA] transition-colors ${belowPar ? 'bg-[#FDF3E1]/20' : ''}`} data-testid={`product-${p.product_id}`}>
                  <td className="px-6 py-3.5">
                    <p className="text-sm font-medium text-slate-900">{p.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <span className="text-xs text-slate-400">{p.category}</span>
                      <span className="text-xs text-rose-400">{margin}% margin</span>
                      {p.storage_location && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium" data-testid={`storage-loc-${p.product_id}`}>
                          📍 {p.storage_location}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3.5 text-xs text-slate-400 font-mono">{p.sku}</td>
                  <td className="px-3 py-3.5 text-xs text-slate-600">{p.vendor || "—"}</td>
                  <td className="px-3 py-3.5 text-sm text-right text-slate-400">${p.cost}</td>
                  <td className="px-3 py-3.5 text-sm text-right font-medium text-slate-900">${p.price}</td>
                  <td className="px-3 py-3.5 text-sm text-right">
                    <span className={`font-medium ${belowPar ? 'text-amber-600' : 'text-slate-900'}`}>{p.stock}</span>
                    {belowPar && <AlertTriangle className="w-3 h-3 text-amber-600 inline ml-1" />}
                  </td>
                  <td className="px-3 py-3.5 text-sm text-right text-slate-400">{p.reorder_point || p.low_stock_threshold || "—"}</td>
                  <td className="px-3 py-3.5 text-sm text-right text-slate-400">{p.reorder_qty || "—"}</td>
                  <td className="px-3 py-3.5 text-sm text-right font-medium text-slate-900">${stockValue.toFixed(0)}</td>
                  <td className="px-3 py-3.5">
                    <div className="flex gap-0.5 justify-end">
                      <button data-testid={`adjust-${p.product_id}`} onClick={() => { setShowAdjust(p); setAdjustQty(0); }} className="p-1.5 rounded-md hover:bg-[#EAF0F6] text-blue-600" title="Adjust stock"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button data-testid={`edit-${p.product_id}`} onClick={() => setEditProduct({...p})} className="p-1.5 rounded-md hover:bg-[#FAFAFA] text-slate-600" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(p.product_id)} className="p-1.5 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recent Activity */}
      {logs.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="inventory-logs">
          <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-4">Recent Adjustments</h3>
          <div className="space-y-2">
            {logs.map((log, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-200 last:border-0">
                <div className="flex items-center gap-2">
                  {log.adjustment > 0 ? <ArrowUp className="w-3.5 h-3.5 text-emerald-600" /> : <ArrowDown className="w-3.5 h-3.5 text-rose-600" />}
                  <span className="text-sm text-slate-900">{log.product_name}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-[#FAFAFA] text-slate-400">{log.reason}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-sm font-medium ${log.adjustment > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{log.adjustment > 0 ? '+' : ''}{log.adjustment}</span>
                  <span className="text-xs text-slate-400 w-20 text-right">Stock: {log.new_stock}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>)}

      {/* New Product Modal */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-[520px] bg-white border-slate-200 rounded-2xl p-8 max-h-[85vh] overflow-y-auto" data-testid="new-product-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">Add Product</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-400 mb-1 block">Name</label><Input data-testid="prod-name" value={newProduct.name} onChange={e => setNewProduct(p => ({...p, name: e.target.value}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-400 mb-1 block">Category</label><Input value={newProduct.category} onChange={e => setNewProduct(p => ({...p, category: e.target.value}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-slate-400 mb-1 block">Price</label><Input type="number" value={newProduct.price} onChange={e => setNewProduct(p => ({...p, price: Number(e.target.value)}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-400 mb-1 block">Cost</label><Input type="number" value={newProduct.cost} onChange={e => setNewProduct(p => ({...p, cost: Number(e.target.value)}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-400 mb-1 block">SKU</label><Input value={newProduct.sku} onChange={e => setNewProduct(p => ({...p, sku: e.target.value}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-slate-400 mb-1 block">Initial Stock</label><Input type="number" value={newProduct.stock} onChange={e => setNewProduct(p => ({...p, stock: Number(e.target.value)}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-400 mb-1 block">PAR Level</label><Input type="number" value={newProduct.reorder_point} onChange={e => setNewProduct(p => ({...p, reorder_point: Number(e.target.value)}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
              <div><label className="text-xs font-medium text-slate-400 mb-1 block">Reorder Qty</label><Input type="number" value={newProduct.reorder_qty} onChange={e => setNewProduct(p => ({...p, reorder_qty: Number(e.target.value)}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
            </div>
            <div><label className="text-xs font-medium text-slate-400 mb-1 block">Vendor</label><Input value={newProduct.vendor} onChange={e => setNewProduct(p => ({...p, vendor: e.target.value}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
            <div>
              <label className="text-xs font-medium text-slate-400 mb-1 block">Storage Location <span className="text-slate-400 font-normal">(where it physically lives in the salon)</span></label>
              <Input data-testid="prod-storage-location" value={newProduct.storage_location}
                onChange={e => setNewProduct(p => ({ ...p, storage_location: e.target.value }))}
                placeholder="e.g. Back shelf A, Color bar drawer 3"
                className="h-10 border-slate-200 rounded-lg text-sm" />
            </div>
            {locations.length > 0 && (
              <div data-testid="prod-locations-picker">
                <label className="text-xs font-medium text-slate-400 mb-1 block">Available at salons <span className="text-slate-400 font-normal">(leave empty = available everywhere)</span></label>
                <div className="flex flex-wrap gap-1.5">
                  {locations.map(loc => {
                    const active = (newProduct.location_ids || []).includes(loc.location_id);
                    return (
                      <button key={loc.location_id} type="button"
                        onClick={() => setNewProduct(p => ({
                          ...p,
                          location_ids: active
                            ? p.location_ids.filter(id => id !== loc.location_id)
                            : [...(p.location_ids || []), loc.location_id],
                        }))}
                        data-testid={`prod-loc-toggle-${loc.location_id}`}
                        className={`text-xs px-2.5 h-7 rounded-full border transition-all ${
                          active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                        }`}>{loc.name}</button>
                    );
                  })}
                </div>
              </div>
            )}
            <Button data-testid="create-product-btn" onClick={handleCreate} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">Add Product</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Product Modal */}
      <Dialog open={!!editProduct} onOpenChange={() => setEditProduct(null)}>
        <DialogContent className="sm:max-w-[520px] bg-white border-slate-200 rounded-2xl p-8 max-h-[85vh] overflow-y-auto" data-testid="edit-product-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">Edit {editProduct?.name}</DialogTitle></DialogHeader>
          {editProduct && (
            <div className="space-y-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-slate-400 mb-1 block">Name</label><Input data-testid="edit-name" value={editProduct.name} onChange={e => setEditProduct(p => ({...p, name: e.target.value}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
                <div><label className="text-xs font-medium text-slate-400 mb-1 block">Category</label><Input value={editProduct.category} onChange={e => setEditProduct(p => ({...p, category: e.target.value}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-xs font-medium text-slate-400 mb-1 block">Price</label><Input data-testid="edit-price" type="number" value={editProduct.price} onChange={e => setEditProduct(p => ({...p, price: Number(e.target.value)}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
                <div><label className="text-xs font-medium text-slate-400 mb-1 block">Cost</label><Input data-testid="edit-cost" type="number" value={editProduct.cost} onChange={e => setEditProduct(p => ({...p, cost: Number(e.target.value)}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
                <div><label className="text-xs font-medium text-slate-400 mb-1 block">SKU</label><Input value={editProduct.sku} onChange={e => setEditProduct(p => ({...p, sku: e.target.value}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-slate-400 mb-1 block">PAR Level (Reorder Point)</label><Input data-testid="edit-par" type="number" value={editProduct.reorder_point || editProduct.low_stock_threshold || 0} onChange={e => setEditProduct(p => ({...p, reorder_point: Number(e.target.value), low_stock_threshold: Number(e.target.value)}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
                <div><label className="text-xs font-medium text-slate-400 mb-1 block">Reorder Quantity</label><Input data-testid="edit-reorder-qty" type="number" value={editProduct.reorder_qty || 0} onChange={e => setEditProduct(p => ({...p, reorder_qty: Number(e.target.value)}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
              </div>
              <div><label className="text-xs font-medium text-slate-400 mb-1 block">Vendor</label><Input value={editProduct.vendor || ""} onChange={e => setEditProduct(p => ({...p, vendor: e.target.value}))} className="h-10 border-slate-200 rounded-lg text-sm" /></div>
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1 block">Storage Location <span className="text-slate-400 font-normal">(in-salon shelf, drawer, etc.)</span></label>
                <Input data-testid="edit-storage-location" value={editProduct.storage_location || ""}
                  onChange={e => setEditProduct(p => ({ ...p, storage_location: e.target.value }))}
                  placeholder="e.g. Back shelf A"
                  className="h-10 border-slate-200 rounded-lg text-sm" />
              </div>
              {locations.length > 0 && (
                <div data-testid="edit-locations-picker">
                  <label className="text-xs font-medium text-slate-400 mb-1 block">Available at salons</label>
                  <div className="flex flex-wrap gap-1.5">
                    {locations.map(loc => {
                      const active = (editProduct.location_ids || []).includes(loc.location_id);
                      return (
                        <button key={loc.location_id} type="button"
                          onClick={() => setEditProduct(p => ({
                            ...p,
                            location_ids: active
                              ? (p.location_ids || []).filter(id => id !== loc.location_id)
                              : [...(p.location_ids || []), loc.location_id],
                          }))}
                          data-testid={`edit-loc-toggle-${loc.location_id}`}
                          className={`text-xs px-2.5 h-7 rounded-full border transition-all ${
                            active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                          }`}>{loc.name}</button>
                      );
                    })}
                  </div>
                </div>
              )}
              <Button data-testid="save-product-btn" onClick={handleUpdate} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Modal */}
      <Dialog open={!!showAdjust} onOpenChange={() => setShowAdjust(null)}>
        <DialogContent className="sm:max-w-[400px] bg-white border-slate-200 rounded-2xl p-8" data-testid="adjust-stock-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">Adjust Stock: {showAdjust?.name}</DialogTitle></DialogHeader>
          {showAdjust && (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">Current stock: <span className="font-medium text-slate-900">{showAdjust.stock}</span> | PAR: <span className="font-medium">{showAdjust.reorder_point || showAdjust.low_stock_threshold}</span></p>
              <div>
                <label className="text-xs font-medium text-slate-400 mb-1 block">Quantity (+/-)</label>
                <Input data-testid="adjust-qty" type="number" value={adjustQty} onChange={e => setAdjustQty(Number(e.target.value))} className="h-11 border-slate-200 rounded-lg" placeholder="e.g., 10 or -5" />
              </div>
              <Select value={adjustReason} onValueChange={setAdjustReason}>
                <SelectTrigger className="h-11 border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="restock">Restock / Received</SelectItem>
                  <SelectItem value="sold">Sold</SelectItem>
                  <SelectItem value="damaged">Damaged / Shrinkage</SelectItem>
                  <SelectItem value="returned">Customer Return</SelectItem>
                  <SelectItem value="count">Physical Count</SelectItem>
                  <SelectItem value="transfer">Location Transfer</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-slate-600">New stock: <span className="font-medium text-slate-900">{Math.max((showAdjust.stock || 0) + adjustQty, 0)}</span></p>
              <Button data-testid="confirm-adjust-btn" onClick={handleAdjust} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">
                {adjustQty >= 0 ? "Add" : "Remove"} {Math.abs(adjustQty)} units
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Purchase Orders Section */}
      {purchaseOrders.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-8" data-testid="purchase-orders-section">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-rose-400" strokeWidth={1.5} />
              <h3 className="font-['Outfit'] text-lg font-medium text-slate-900">Purchase Orders</h3>
              <span className="text-xs text-slate-400">{purchaseOrders.length} orders</span>
            </div>
          </div>
          <div className="divide-y divide-[#E6E4E0]">
            {purchaseOrders.map(po => (
              <div key={po.po_id} className="p-5 hover:bg-[#FAFAFA] transition-colors" data-testid={`po-${po.po_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-slate-900">{po.po_id}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        po.status === "draft" ? "bg-[#FAFAFA] text-slate-600" :
                        po.status === "sent" ? "bg-[#EAF0F6] text-blue-600" :
                        po.status === "received" ? "bg-[#E8F0E9] text-emerald-600" :
                        "bg-[#FDF3E1] text-amber-600"
                      }`}>{po.status}</span>
                      {po.auto_generated && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5CF615] text-[#8B5CF6]">auto</span>}
                    </div>
                    <p className="text-sm text-slate-600 mt-0.5">
                      <span className="font-medium text-slate-900">{po.vendor}</span>
                      {po.vendor_email && <span className="text-slate-400 ml-2">{po.vendor_email}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-['Outfit'] text-lg font-medium text-slate-900">${po.total_cost}</p>
                    <p className="text-xs text-slate-400">{po.total_items} items, {po.total_units} units</p>
                  </div>
                </div>
                {/* PO Line Items */}
                <div className="bg-[#FAFAFA] rounded-lg p-3 mb-3">
                  <div className="space-y-1.5">
                    {(po.items || []).map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-900">{item.name}</span>
                          <span className="text-[10px] font-mono text-slate-400">{item.sku}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white text-amber-600">
                            Stock: {item.current_stock} / PAR: {item.reorder_point}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-slate-600">{item.reorder_qty} x ${item.unit_cost}</span>
                          <span className="font-medium text-slate-900">${item.line_total}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* PO Actions */}
                <div className="flex gap-2">
                  {po.status === "draft" && (
                    <Button data-testid={`approve-po-${po.po_id}`} onClick={() => approvePO(po.po_id)} size="sm" className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-8 px-4 text-xs">
                      <Send className="w-3 h-3 mr-1" /> Send to Vendor
                    </Button>
                  )}
                  {po.status === "sent" && (
                    <Button data-testid={`receive-po-${po.po_id}`} onClick={() => receivePO(po.po_id)} size="sm" className="bg-emerald-600 text-white hover:bg-[#1e3f24] rounded-full h-8 px-4 text-xs">
                      <Check className="w-3 h-3 mr-1" /> Mark Received
                    </Button>
                  )}
                  {po.status !== "received" && (
                    <Button onClick={() => deletePO(po.po_id)} size="sm" variant="outline" className="border-slate-200 rounded-full h-8 px-3 text-xs text-slate-400">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                  <span className="text-[10px] text-slate-400 ml-auto self-center">
                    {po.created_at ? new Date(po.created_at).toLocaleDateString() : ""}
                    {po.min_threshold > 0 && ` | Min: $${po.min_threshold}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Auto-Generate Result Dialog */}
      <Dialog open={!!genResult} onOpenChange={() => setGenResult(null)}>
        <DialogContent className="sm:max-w-[500px] bg-white border-slate-200 rounded-2xl p-8" data-testid="gen-result-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">Auto-Reorder Result</DialogTitle></DialogHeader>
          {genResult && (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">{genResult.message}</p>
              {genResult.purchase_orders?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-emerald-600 mb-2">Generated ({genResult.generated})</p>
                  {genResult.purchase_orders.map(po => (
                    <div key={po.po_id} className="flex items-center justify-between py-2 border-b border-slate-200">
                      <span className="text-sm font-medium text-slate-900">{po.vendor}</span>
                      <span className="text-sm text-slate-900">${po.total_cost} ({po.total_items} items)</span>
                    </div>
                  ))}
                </div>
              )}
              {genResult.skipped_details?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-amber-600 mb-2">Skipped ({genResult.skipped})</p>
                  {genResult.skipped_details.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-slate-200">
                      <span className="text-sm text-slate-600">{s.vendor}</span>
                      <span className="text-xs text-slate-400">{s.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Reorder Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-[560px] bg-white border-slate-200 rounded-2xl p-8 max-h-[85vh] overflow-y-auto" data-testid="vendor-settings-modal">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">Vendor Reorder Rules</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 mt-2 mb-4">Set minimum order thresholds per vendor. POs won't auto-generate unless the total meets the vendor minimum.</p>
          <div className="space-y-3">
            {reorderSettings.map((setting, i) => (
              <div key={setting.vendor} className="bg-[#FAFAFA] rounded-lg p-4" data-testid={`vendor-rule-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-900">{setting.vendor}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Auto-reorder</span>
                    <Switch checked={setting.auto_reorder !== false}
                      onCheckedChange={async (v) => {
                        await api.post("/reorder-settings", { ...setting, auto_reorder: v });
                        fetchData();
                      }} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold uppercase text-slate-400">Min Order Total ($)</label>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-sm text-slate-600">$</span>
                      <Input
                        data-testid={`min-threshold-${i}`}
                        type="number"
                        defaultValue={setting.min_order_total || 0}
                        onBlur={async (e) => {
                          const val = Number(e.target.value);
                          if (val !== setting.min_order_total) {
                            await api.post("/reorder-settings", { ...setting, min_order_total: val });
                            toast.success(`Minimum updated for ${setting.vendor}`);
                            fetchData();
                          }
                        }}
                        className="h-8 border-slate-200 rounded-md text-sm w-24"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-slate-400">Vendor Email</label>
                    <p className="text-sm text-slate-600 mt-1">{setting.vendor_email || "Not set"}</p>
                  </div>
                </div>
              </div>
            ))}
            {reorderSettings.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-4">No vendors configured yet. Add vendors to products first.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <BulkImportDialog open={showImport} onOpenChange={setShowImport} type="products" onDone={fetchData} />
      <ImportHistoryDialog open={showImportHistory} onOpenChange={setShowImportHistory} onChange={fetchData} />

      {/* ────────── Quick Check-In Drawer ────────── */}
      {/* Side-panel for receiving a vendor delivery without opening N edit
          dialogs. Search → tap +/- (or type) → submit all in one call. */}
      {showQuickReceive && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/40"
          onClick={() => !receiveSubmitting && setShowQuickReceive(false)}
          data-testid="quick-receive-drawer">
          <div className="bg-white w-full max-w-2xl flex flex-col shadow-xl" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">Quick Check-In</h2>
                  <p className="text-[11px] text-slate-500">Receive a vendor delivery — tap +/− to add to stock</p>
                </div>
              </div>
              <button data-testid="quick-receive-close"
                onClick={() => !receiveSubmitting && setShowQuickReceive(false)}
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Search */}
            <div className="px-6 pt-4 pb-3 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input data-testid="quick-receive-search" value={receiveSearch} onChange={e => setReceiveSearch(e.target.value)}
                  placeholder="Search products by name or SKU..." autoFocus
                  className="pl-10 h-10 border-slate-200 rounded-lg" />
              </div>
              {Object.keys(receiveCart).filter(id => receiveCart[id] > 0).length > 0 && (
                <div className="flex items-center justify-between mt-3">
                  <p className="text-[11px] text-slate-500">
                    {Object.values(receiveCart).filter(q => q > 0).length} product{Object.values(receiveCart).filter(q => q > 0).length === 1 ? "" : "s"} ·{" "}
                    {Object.values(receiveCart).reduce((sum, q) => sum + (Number(q) > 0 ? Number(q) : 0), 0)} unit
                    {Object.values(receiveCart).reduce((sum, q) => sum + (Number(q) > 0 ? Number(q) : 0), 0) === 1 ? "" : "s"} ready to receive
                  </p>
                  <button data-testid="quick-receive-clear"
                    onClick={() => setReceiveCart({})}
                    className="text-[11px] text-rose-600 hover:underline">Clear</button>
                </div>
              )}
            </div>

            {/* Product list */}
            <div className="flex-1 overflow-y-auto px-6 py-2">
              {(() => {
                const list = products
                  .filter(p => !receiveSearch || p.name?.toLowerCase().includes(receiveSearch.toLowerCase()) || p.sku?.toLowerCase().includes(receiveSearch.toLowerCase()))
                  .sort((a, b) => {
                    // Items in the cart float to the top so the user sees what's pending
                    const aIn = (receiveCart[a.product_id] || 0) > 0 ? 0 : 1;
                    const bIn = (receiveCart[b.product_id] || 0) > 0 ? 0 : 1;
                    if (aIn !== bIn) return aIn - bIn;
                    return (a.name || "").localeCompare(b.name || "");
                  })
                  .slice(0, 80);
                if (list.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-400">No products match "{receiveSearch}"</p>
                    </div>
                  );
                }
                return list.map(p => {
                  const qty = Number(receiveCart[p.product_id] || 0);
                  const par = p.reorder_point || p.low_stock_threshold || 0;
                  const belowPar = par > 0 && (p.stock || 0) <= par;
                  return (
                    <div key={p.product_id}
                      data-testid={`receive-row-${p.product_id}`}
                      className={`flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-b-0 ${qty > 0 ? "bg-emerald-50/40 -mx-2 px-2 rounded-lg" : ""}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{p.name}</p>
                        <p className="text-[11px] text-slate-400">
                          <span className="font-mono">{p.sku || "—"}</span>
                          {" · "}Current: <span className={belowPar ? "text-amber-600 font-semibold" : "text-slate-600"}>{p.stock || 0}</span>
                          {par > 0 ? <> · PAR: {par}</> : null}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button data-testid={`receive-dec-${p.product_id}`}
                          onClick={() => setReceiveCart(c => ({ ...c, [p.product_id]: Math.max((Number(c[p.product_id]) || 0) - 1, 0) }))}
                          disabled={qty === 0}
                          className="w-8 h-8 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center">
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <Input
                          data-testid={`receive-qty-${p.product_id}`}
                          type="number" inputMode="numeric" min={0}
                          value={qty || ""}
                          onChange={e => {
                            const v = e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0);
                            setReceiveCart(c => ({ ...c, [p.product_id]: v }));
                          }}
                          placeholder="0"
                          className="w-16 h-8 text-sm text-center border-slate-200 rounded-md px-1" />
                        <button data-testid={`receive-inc-${p.product_id}`}
                          onClick={() => setReceiveCart(c => ({ ...c, [p.product_id]: (Number(c[p.product_id]) || 0) + 1 }))}
                          className="w-8 h-8 rounded-md border border-emerald-200 bg-emerald-500 text-white hover:bg-emerald-600 flex items-center justify-center">
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {qty > 0 && (
                        <p className="text-[11px] text-emerald-700 font-semibold whitespace-nowrap w-16 text-right">
                          → {(p.stock || 0) + qty}
                        </p>
                      )}
                    </div>
                  );
                });
              })()}
            </div>

            {/* Footer / submit */}
            <div className="border-t border-slate-200 px-6 py-4 flex items-center justify-between bg-[#FAFAFA]">
              <Button variant="outline" onClick={() => setShowQuickReceive(false)} disabled={receiveSubmitting}
                className="border-slate-200 rounded-full h-10 px-5">Cancel</Button>
              <Button data-testid="quick-receive-submit"
                onClick={submitReceiveCart}
                disabled={receiveSubmitting || Object.values(receiveCart).every(q => !q || q <= 0)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 px-6">
                {receiveSubmitting ? "Receiving..." : `Receive ${Object.values(receiveCart).filter(q => q > 0).length || ""} product${Object.values(receiveCart).filter(q => q > 0).length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Storage Map ──────────────────────────────────────────────────
// Groups every product by its in-salon `storage_location` so staff can
// answer "where did Sarah put the new shipment?" or "what's on Back
// Shelf A?" without scanning the whole inventory table. Auto-respects
// the active salon-location chip from the parent page.
function StorageMapTab({ products, activeLocationId, locations, onJump }) {
  const scoped = products.filter(p => {
    if (activeLocationId === "all") return true;
    const lids = p.location_ids || [];
    return lids.length === 0 || lids.includes(activeLocationId);
  });

  // Group by storage_location; products with no storage_location land
  // in an "Unassigned" bucket so the salon can see what still needs to
  // be filed.
  const groups = {};
  for (const p of scoped) {
    const key = (p.storage_location || "").trim() || "__unassigned__";
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  }
  const entries = Object.entries(groups).sort(([a], [b]) => {
    if (a === "__unassigned__") return 1;
    if (b === "__unassigned__") return -1;
    return a.localeCompare(b);
  });

  const fmt$ = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const activeLocName = activeLocationId === "all"
    ? "all locations"
    : (locations.find(l => l.location_id === activeLocationId)?.name || "this location");

  if (scoped.length === 0) {
    return (
      <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400">
        No products found for {activeLocName}.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="storage-map-tab">
      <p className="text-xs text-slate-500">
        Showing {scoped.length} products across {entries.length} storage location{entries.length !== 1 ? "s" : ""} · {activeLocName}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {entries.map(([key, items]) => {
          const isUnassigned = key === "__unassigned__";
          const totalUnits = items.reduce((s, p) => s + (p.stock || 0), 0);
          const totalValue = items.reduce((s, p) => s + (p.stock || 0) * (p.cost || 0), 0);
          const belowParCount = items.filter(p => {
            const par = p.reorder_point || p.low_stock_threshold || 0;
            return par > 0 && (p.stock || 0) <= par;
          }).length;
          return (
            <div key={key}
              data-testid={`storage-bucket-${isUnassigned ? "unassigned" : key.replace(/\s+/g, "-").toLowerCase()}`}
              className={`bg-white border rounded-2xl p-5 hover:shadow-sm transition-shadow ${isUnassigned ? "border-amber-200 bg-amber-50/20" : "border-slate-200"}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0 flex-1">
                  <p className={`text-[10px] font-bold uppercase tracking-widest ${isUnassigned ? "text-amber-700" : "text-slate-400"}`}>
                    {isUnassigned ? "Not yet assigned" : "Storage"}
                  </p>
                  <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 truncate" title={isUnassigned ? "Unassigned" : key}>
                    {isUnassigned ? "Unassigned" : `📍 ${key}`}
                  </h3>
                </div>
                {!isUnassigned && (
                  <button onClick={() => onJump(key)}
                    data-testid={`jump-stock-${key.replace(/\s+/g, "-").toLowerCase()}`}
                    className="text-[10px] uppercase tracking-widest font-bold text-rose-600 hover:text-rose-700 shrink-0 ml-2">
                    View in Stock →
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[9px] text-slate-400 uppercase tracking-widest">Products</p>
                  <p className="font-['Outfit'] text-lg font-semibold text-slate-900">{items.length}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[9px] text-slate-400 uppercase tracking-widest">Units</p>
                  <p className="font-['Outfit'] text-lg font-semibold text-slate-900 tabular-nums">{totalUnits}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[9px] text-slate-400 uppercase tracking-widest">Cost value</p>
                  <p className="font-['Outfit'] text-lg font-semibold text-emerald-700 tabular-nums">{fmt$(totalValue)}</p>
                </div>
              </div>
              {belowParCount > 0 && (
                <div className="mb-2 text-[11px] px-2 py-1 rounded-md bg-amber-100 text-amber-800 font-medium inline-block">
                  ⚠ {belowParCount} below PAR
                </div>
              )}
              <ul className="space-y-1 text-sm max-h-44 overflow-y-auto">
                {items.slice(0, 12).map(p => {
                  const par = p.reorder_point || p.low_stock_threshold || 0;
                  const low = par > 0 && (p.stock || 0) <= par;
                  return (
                    <li key={p.product_id} className="flex items-center justify-between gap-2 text-slate-700">
                      <span className="truncate" title={p.name}>{p.name}</span>
                      <span className={`text-xs tabular-nums shrink-0 ${low ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                        {p.stock || 0}
                      </span>
                    </li>
                  );
                })}
                {items.length > 12 && (
                  <li className="text-xs text-slate-400 pt-1">+ {items.length - 12} more</li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

